import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Rocket, Calendar, ArrowRight } from "lucide-react";

export default function LaunchCenter() {
  const [items, setItems] = useState(null);

  useEffect(() => { api.get("/ops/content").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  if (!items) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  const withDates = items.filter((i) => i.publish_date && ["scheduled", "approved", "ready", "published"].includes(i.status));
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = withDates.filter((i) => i.publish_date >= today).sort((a, b) => a.publish_date.localeCompare(b.publish_date));
  const past = withDates.filter((i) => i.publish_date < today).sort((a, b) => b.publish_date.localeCompare(a.publish_date));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight">Launch Center</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Your marketing calendar — everything scheduled to go out, and what already shipped.</p>
      </div>

      <Section icon={Calendar} title={`Upcoming (${upcoming.length})`} testid="launch-upcoming">
        {upcoming.length === 0 ? (
          <Empty>Nothing scheduled yet. Add a publish date to content in the <Link to="/ops/content" className="text-indigo-500 hover:underline">Content Library</Link>.</Empty>
        ) : upcoming.map((i) => <Row key={i.id} item={i} accent />)}
      </Section>

      <Section icon={Rocket} title={`Recently launched (${past.length})`} testid="launch-past">
        {past.length === 0 ? <Empty>No launches recorded yet.</Empty> : past.slice(0, 20).map((i) => <Row key={i.id} item={i} />)}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-indigo-500" />
        <h2 className="font-heading font-bold">{title}</h2>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function Row({ item, accent }) {
  return (
    <div className="flex items-center gap-4 p-4">
      <div className={`w-14 shrink-0 text-center rounded-lg py-1.5 ${accent ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`}>
        <div className="text-[10px] uppercase">{new Date(item.publish_date).toLocaleDateString("en-US", { month: "short" })}</div>
        <div className="font-heading font-black text-lg leading-none">{new Date(item.publish_date).getDate()}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{item.title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 capitalize">{item.type} · {item.status}{item.related_product ? ` · ${item.related_product}` : ""}</div>
      </div>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-slate-400 p-6">{children}</p>;
}
